import { useCallback, useRef } from "react";

const DOUBLE_TAP_DELAY = 300; // ms between taps
const MOVEMENT_THRESHOLD = 30; // px — ignore if finger moved (drag gesture)

interface DoubleTapHandlers {
  onDoubleClick: (e: React.MouseEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
}

/**
 * Returns event handlers for detecting double-click (desktop) and double-tap (mobile).
 * Distinguishes taps from drag gestures using a movement threshold.
 */
export function useDoubleTap(callback: () => void): DoubleTapHandlers {
  const lastTap = useRef<{ time: number; x: number; y: number } | null>(null);

  const onDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      // Only trigger on the header background, not on interactive children
      const target = e.target as HTMLElement;
      if (target.closest("button, input, select, textarea, [role=button]")) return;
      callback();
    },
    [callback]
  );

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("button, input, select, textarea, [role=button]")) return;

      const touch = e.changedTouches[0];
      const now = Date.now();

      if (lastTap.current) {
        const dt = now - lastTap.current.time;
        const dx = Math.abs(touch.clientX - lastTap.current.x);
        const dy = Math.abs(touch.clientY - lastTap.current.y);

        if (dt < DOUBLE_TAP_DELAY && dx < MOVEMENT_THRESHOLD && dy < MOVEMENT_THRESHOLD) {
          e.preventDefault(); // prevent zoom on double-tap
          lastTap.current = null;
          callback();
          return;
        }
      }

      lastTap.current = { time: now, x: touch.clientX, y: touch.clientY };
    },
    [callback]
  );

  return { onDoubleClick, onTouchEnd };
}
